import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { employeesAPI } from "../api";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  ArrowLeft, Star, TrendingUp, TrendingDown, Minus,
  AlertCircle, CheckCircle2, Clock, ChevronLeft, ChevronRight, ChevronDown,
} from "lucide-react";
import { motion } from "framer-motion";
import DateRangePicker from "../components/DateRangePicker";
import { startOfDay, endOfDay, subDays } from "date-fns";

function ScoreCard({ label, score, change, max = 5 }) {
  const pct = (score / max) * 100;
  const color = score >= 4 ? "text-green-600" : score >= 3 ? "text-amber-600" : "text-red-600";
  const bgColor = score >= 4 ? "bg-green-500" : score >= 3 ? "bg-amber-500" : "bg-red-500";

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <div className="flex items-end space-x-2 mt-1">
        <p className={`text-3xl font-bold ${color}`}>{score.toFixed(1)}</p>
        <p className="text-sm text-gray-400 mb-1">/ {max}</p>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
        <div className={`${bgColor} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      {change !== undefined && change !== 0 && (
        <div className={`flex items-center mt-2 text-xs ${change > 0 ? "text-green-600" : "text-red-600"}`}>
          {change > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
          {change > 0 ? "+" : ""}{change}% vs kỳ trước
        </div>
      )}
    </div>
  );
}

function TrendIcon({ trend }) {
  if (trend === "improving") return <TrendingUp className="w-4 h-4 text-green-600" />;
  if (trend === "declining") return <TrendingDown className="w-4 h-4 text-red-600" />;
  return <Minus className="w-4 h-4 text-gray-400" />;
}

function TrendLabel({ trend }) {
  const map = { improving: "Cải thiện", declining: "Giảm sút", stable: "Ổn định" };
  const color = { improving: "text-green-600", declining: "text-red-600", stable: "text-gray-500" };
  return <span className={`text-sm font-medium ${color[trend] || "text-gray-500"}`}>{map[trend] || trend}</span>;
}

function StarRating({ score }) {
  return (
    <div className="flex items-center space-x-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i <= score ? "text-amber-400 fill-amber-400" : "text-gray-200"}`}
        />
      ))}
    </div>
  );
}

export default function AgentProfilePage() {
  const { agentId } = useParams();
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [profile, setProfile] = useState(null);
  const [trend, setTrend] = useState([]);
  const [improvements, setImprovements] = useState({ open: [], resolved: [] });
  const [periodReviews, setPeriodReviews] = useState([]);
  const [evaluations, setEvaluations] = useState({ items: [], total: 0, page: 1 });
  const [evalPage, setEvalPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState("week");
  const [resolvedLimit, setResolvedLimit] = useState(10);

  const dateParams = useCallback(() => {
    return { date_from: startOfDay(startDate).toISOString(), date_to: endOfDay(endDate).toISOString() };
  }, [startDate, endDate]);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const params = dateParams();
      const [profileRes, trendRes, improvRes, reviewsRes, evalsRes] = await Promise.all([
        employeesAPI.profile(agentId, params),
        employeesAPI.trend(agentId, { ...params, group_by: groupBy }),
        employeesAPI.improvements(agentId, params),
        employeesAPI.periodReviews(agentId, { date_from: new Date(Date.now() - 365 * 86400000).toISOString() }),
        employeesAPI.evaluations(agentId, { ...params, page: 1, limit: 10 }),
      ]);
      setProfile(profileRes.data);
      setTrend(trendRes.data || []);
      setImprovements(improvRes.data || { open: [], resolved: [] });
      setResolvedLimit(10);
      setPeriodReviews(reviewsRes.data || []);
      setEvaluations(evalsRes.data || { items: [], total: 0, page: 1 });
      setEvalPage(1);
    } catch (err) {
      console.error("Agent profile fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [agentId, startDate, endDate, groupBy, dateParams]);

  const handleDateChange = useCallback(({ startDate: s, endDate: e }) => {
    setStartDate(s);
    setEndDate(e);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadEvalPage = async (page) => {
    try {
      const params = dateParams();
      const res = await employeesAPI.evaluations(agentId, { ...params, page, limit: 10 });
      setEvaluations(res.data || { items: [], total: 0, page });
      setEvalPage(page);
    } catch (err) {
      console.error("Eval page error:", err);
    }
  };

  const agent = profile?.agent || {};
  const stats = profile?.stats || {};
  const changes = profile?.changes || {};
  const totalEvalPages = Math.ceil((evaluations.total || 0) / 10);

  return (
    <div className="space-y-6">
      {/* Header — always mounted so DateRangePicker keeps state */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <button onClick={() => navigate("/employees")} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
            <span className="text-primary-700 font-bold text-lg">{(agent.name || "?").charAt(0)}</span>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900">{agent.name || "—"}</h3>
            <p className="text-sm text-gray-500">{agent.email || ""}</p>
          </div>
        </div>
        <DateRangePicker
          onChange={handleDateChange}
          initialDays={30}
          storageKey="agent_profile_date_range"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
        </div>
      ) : (<>

        {/* Score Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <ScoreCard label="Hài lòng KH" score={stats.avg_satisfaction || 0} change={changes.satisfaction_change} />
          <ScoreCard label="Thái độ NV" score={stats.avg_attitude || 0} change={changes.attitude_change} />
          <ScoreCard label="Chất lượng GQ" score={stats.avg_resolution || 0} change={changes.resolution_change} />
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <p className="text-sm text-gray-500 font-medium">Tổng hội thoại</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total_conversations || 0}</p>
            {changes.conv_change !== undefined && changes.conv_change !== 0 && (
              <div className={`flex items-center mt-2 text-xs ${changes.conv_change > 0 ? "text-green-600" : "text-red-600"}`}>
                {changes.conv_change > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                {changes.conv_change > 0 ? "+" : ""}{changes.conv_change}% vs kỳ trước
              </div>
            )}
          </div>
        </div>

        {/* Trend Chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-base font-semibold text-gray-900">Biểu đồ tiến trình</h4>
            <div className="flex items-center space-x-1">
              {[{ v: "day", l: "Ngày" }, { v: "week", l: "Tuần" }, { v: "month", l: "Tháng" }].map((g) => (
                <button
                  key={g.v}
                  onClick={() => setGroupBy(g.v)}
                  className={`px-2 py-1 rounded text-xs font-medium ${groupBy === g.v ? "bg-primary-100 text-primary-700" : "text-gray-500 hover:bg-gray-100"
                    }`}
                >
                  {g.l}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 11 }}
                stroke="#94a3b8"
                tickFormatter={(v) => {
                  try {
                    const d = new Date(v);
                    return `${d.getDate()}/${d.getMonth() + 1}`;
                  } catch {
                    return v;
                  }
                }}
              />
              <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip
                contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}
                formatter={(v) => v?.toFixed(2)}
              />
              <Legend />
              <Line type="monotone" dataKey="avg_satisfaction" name="Hài lòng" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="avg_attitude" name="Thái độ" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="avg_resolution" name="Chất lượng" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Improvements */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* Open */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card">
            <div className="flex items-center space-x-2 mb-4">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <h4 className="text-base font-semibold text-gray-900">Cần cải thiện ({improvements.open?.length || 0})</h4>
            </div>
            <div className="space-y-3 max-h-[28rem] overflow-y-auto">
              {(improvements.open || []).length === 0 && (
                <p className="text-sm text-gray-400 italic">Không có vấn đề nào đang mở</p>
              )}
              {(improvements.open || []).map((item) => (
                <div key={item.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${item.status === "improving" ? "bg-amber-50 border-amber-200 hover:border-amber-400" : "bg-red-50 border-red-200 hover:border-red-400"
                    }`}
                  onClick={() => navigate(`/conversations?improvement_category=${encodeURIComponent(item.category_label)}`)}
                  title={`Nhấp để xem các cuộc hội thoại liên quan đến "${item.category_label}"`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{item.category_label}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {item.occurrence_count} lần · từ {item.first_found_at ? new Date(item.first_found_at).toLocaleDateString("vi-VN") : "—"}
                      </p>
                    </div>
                    {item.status === "improving" && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Đang cải thiện</span>
                    )}
                  </div>
                  {item.consecutive_clean > 0 && (
                    <div className="mt-2 flex items-center space-x-1">
                      <div className="flex space-x-0.5">
                        {[...Array(5)].map((_, i) => (
                          <div key={i} className={`w-4 h-1.5 rounded-full ${i < item.consecutive_clean ? "bg-green-400" : "bg-gray-200"}`} />
                        ))}
                      </div>
                      <span className="text-xs text-gray-400">{item.consecutive_clean}/5 sạch</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Resolved */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card">
            <div className="flex items-center space-x-2 mb-4">
              <CheckCircle2 className="w-5 h-5 text-green-500" />
              <h4 className="text-base font-semibold text-gray-900">Đã khắc phục ({improvements.resolved?.length || 0})</h4>
            </div>
            <div className="space-y-3 max-h-[28rem] overflow-y-auto">
              {(improvements.resolved || []).length === 0 && (
                <p className="text-sm text-gray-400 italic">Chưa có vấn đề nào được khắc phục trong khoảng này</p>
              )}
              {(improvements.resolved || []).slice(0, resolvedLimit).map((item) => (
                <div key={item.id} className="p-3 rounded-lg border bg-green-50 border-green-200">
                  <p className="text-sm font-medium text-gray-800 line-through decoration-green-400">{item.category_label}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Mở {item.first_found_at ? new Date(item.first_found_at).toLocaleDateString("vi-VN") : "—"} →
                    Khắc phục {item.resolved_at ? new Date(item.resolved_at).toLocaleDateString("vi-VN") : "—"}
                    {item.occurrence_count > 1 && ` · ${item.occurrence_count} lần`}
                  </p>
                </div>
              ))}
            </div>
            {(improvements.resolved || []).length > resolvedLimit && (
              <button
                onClick={() => setResolvedLimit((prev) => prev + 20)}
                className="mt-3 w-full flex items-center justify-center gap-1 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg font-medium transition-colors"
              >
                <ChevronDown className="w-4 h-4" />
                Xem thêm ({(improvements.resolved || []).length - resolvedLimit} còn lại)
              </button>
            )}
          </motion.div>
        </div>


        {/* Evaluations List */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card">
          <h4 className="text-base font-semibold text-gray-900 mb-4">
            Chi tiết đánh giá từng cuộc hội thoại ({evaluations.total || 0})
          </h4>
          <div className="space-y-4">
            {(evaluations.items || []).map((ev) => (
              <div key={ev.id} className="p-4 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-500">
                    {ev.conversation_started_at
                      ? new Date(ev.conversation_started_at).toLocaleString("vi-VN")
                      : ev.evaluated_at
                        ? new Date(ev.evaluated_at).toLocaleString("vi-VN")
                        : "—"}
                  </span>
                  <button
                    onClick={() => navigate(`/conversations/${ev.conversation_id}`, { state: { from: `/employees/${agentId}` } })}
                    className="text-xs text-primary-600 hover:underline font-medium"
                  >
                    Conv #{ev.conversation_id} →
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">Hài lòng:</span>
                      <StarRating score={ev.satisfaction_score || 0} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 italic">{ev.satisfaction_reason}</p>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">Thái độ:</span>
                      <StarRating score={ev.agent_attitude_score || 0} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 italic">{ev.agent_attitude_reason}</p>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">Chất lượng:</span>
                      <StarRating score={ev.resolution_quality_score || 0} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 italic">{ev.resolution_quality_reason}</p>
                  </div>
                </div>
                {ev.summary && (
                  <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded mb-2">{ev.summary}</p>
                )}
                {ev.improvement_categories?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {ev.improvement_categories.map((c) => (
                      <span key={c} className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full">{c}</span>
                    ))}
                  </div>
                )}
                {ev.improvement_suggestions?.length > 0 && (
                  <div className="mt-2">
                    {ev.improvement_suggestions.map((s, i) => (
                      <p key={i} className="text-xs text-amber-700">💡 {s}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalEvalPages > 1 && (
            <div className="flex items-center justify-center space-x-2 mt-4 pt-4 border-t border-gray-100">
              <button
                onClick={() => loadEvalPage(Math.max(1, evalPage - 1))}
                disabled={evalPage === 1}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">Trang {evalPage} / {totalEvalPages}</span>
              <button
                onClick={() => loadEvalPage(Math.min(totalEvalPages, evalPage + 1))}
                disabled={evalPage === totalEvalPages}
                className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </motion.div>
      </>)}
    </div>
  );
}
